<script lang="ts">
	import classNames from 'classnames';
	import { getAuthContext } from './AuthDialogs/utils/getAuthContext/getAuthContext';
	import { NewsHeaderIcon } from './NewsHeaderIcon';
	import { NewsWhatsappButton } from './NewsWhatsappButton';
	import { SavedTagsDrawer } from './SavedTagsDrawer';
	import { Config } from '#lib/constants/config.js';
	import { Links } from '#lib/constants/links.js';
	import { NavBar } from '#lib/features/navigation/components/NavBar/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { preserveGlobalNav } from '#lib/features/navigation/utils/preserveGlobalNav/preserveGlobalNav.js';
	import { NewsSearch } from '#lib/websites/news/components/NewsHeader/NewsSearch/index.js';
	import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
	import { hasStoredCognitoSession } from '#lib/websites/news/utils/hasStoredCognitoSession/hasStoredCognitoSession.js';
	import { loadCognitoUser } from '#lib/websites/news/utils/loadCognitoUser/loadCognitoUser.js';
	import { loadNewsAuth } from '#lib/websites/news/utils/loadNewsAuth/loadNewsAuth.js';
	import { page } from '$app/state';
	import { AccountCloseIcon } from '$global/assets/icons/AccountCloseIcon';
	import { AccountIcon } from '$global/assets/icons/AccountIcon';
	import { DocumentIcon } from '$global/assets/icons/DocumentIcon';
	import { DownloadIcon } from '$global/assets/icons/DownloadIcon';
	import { HeartIcon } from '$global/assets/icons/HeartIcon';
	import { InfoIcon } from '$global/assets/icons/InfoIcon';
	import { LogOutIcon } from '$global/assets/icons/LogOutIcon';
	import { MailIcon } from '$global/assets/icons/MailIcon';
	import { PrivacyIcon } from '$global/assets/icons/PrivacyIcon';
	import { RefreshIcon } from '$global/assets/icons/RefreshIcon';
	import { TextIcon } from '$global/assets/icons/TextIcon';
	import { WhatsappIcon } from '$global/assets/icons/WhatsappIcon';
	import { Toasts } from '$global/components/Toasts';
	import {
		NEWSLETTER_WHATSAPP_LINK,
		NEWSLETTER_WHATSAPP_TEXT,
	} from '$global/features/newsletter-generation/constants/whatsapp';
	import { createSearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { getAnalytics } from '$global/utils/track';
	import {
		AuthAction,
		AuthMethod,
		AuthProvenance,
		AuthReason,
		trackAuthEvent,
	} from '$global/utils/trackAuthEvent';

	interface Props {
		mounted: boolean;
		forceShowGlobalNav?: boolean;
	}

	let { mounted, forceShowGlobalNav }: Props = $props();

	const combobox = createSearchCombobox();

	const {
		changePasswordDialogStores,
		closeAccountDialogStores,
		loginDialogStores,
		createAccountDialogStores,
		newsletterDialogStores,
		user,
		showSavedTags,
		hasLoadedCredentials,
		hasLoadedUserPrefs,
		isRestoringSession,
		savedTagsDrawerStores,
		mobileMenuPopover,
		showOverlay,
	} = getAuthContext();

	// The auth dialogs (and their forms and zod schemas) are only fetched once
	// one of them is opened, from a button or a URL action. `showOverlay` tracks
	// every auth dialog's open state; once loaded they stay mounted.
	let authDialogsNeeded = $state(false);

	$effect(() => {
		if ($showOverlay) {
			authDialogsNeeded = true;
		}
	});

	// No session to restore after all, so stop withholding the bar.
	const endSessionRestore = () => {
		isRestoringSession.set(false);
		hasLoadedUserPrefs.set(true);
	};

	const loadCredentials = async () => {
		try {
			// Without a stored session Amplify would only fail to find a user, so
			// signed-out visitors skip loading it altogether (see loadNewsAuth).
			const userObj = hasStoredCognitoSession()
				? await loadCognitoUser()
				: null;

			if (userObj) {
				user.set(userObj);
			} else {
				endSessionRestore();
			}
		} catch {
			endSessionRestore();
		} finally {
			hasLoadedCredentials.set(true);
		}
	};

	$effect(() => {
		if (mounted) {
			loadCredentials();
		}
	});

	const handleOpenChangePasswordDialog = () => {
		changePasswordDialogStores.states.open.set(true);
	};

	const handleOpenCloseAccountDialog = () => {
		closeAccountDialogStores.states.open.set(true);
	};

	const handleCreateAccountDialogStores = () => {
		createAccountDialogStores.states.open.set(true);
	};

	const handleLogOut = async () => {
		await (await loadNewsAuth()).signOut();

		// Before op.clear(), which drops the identified profile — afterwards the
		// event would land on an anonymous device instead of the person leaving.
		trackAuthEvent({
			action: AuthAction.Logout,
			method: AuthMethod.Password,
			provenance: AuthProvenance.News,
			reason: AuthReason.UserInitiated,
		});

		getAnalytics()?.clear();
		user.set(null);
	};

	const handleOpenNewsletterDialog = () => {
		newsletterDialogStores.states.open.set(true);
	};

	const handleOpenLoginDialog = () => {
		loginDialogStores.states.open.set(true);
	};

	let showSiteLinks = $derived(page.data.showSiteLinks ?? true);
	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and app-mode
	// (global_nav=0) visitors must not be shown the global nav sections.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			page.url.searchParams.get('global_nav') !== '0'
	);

	let showSearch = $derived(page.data.showSearch ?? true);

	// App-webview visitors (global_nav=0) land on the -mobile variants, but those
	// pages only drop the global nav when the param survives the hop — without it
	// the terms/privacy page renders the hamburger menu the app must not show.
	let termsHref = $derived(
		preserveGlobalNav(
			`${Config.Domain}${showGlobalNav ? NewsRoutes.Terms : NewsRoutes.TermsMobile}`,
			page.url
		)
	);

	let privacyHref = $derived(
		preserveGlobalNav(
			`${Config.Domain}${
				showGlobalNav ? GlobalRoutes.PrivacyUs : GlobalRoutes.PrivacyUsMobile
			}`,
			page.url
		)
	);

	const className = 'border-b border-gray-200 [&_p]:hover:no-underline';

	// Same number as the newsletter footer's CTA, so the two surfaces stay in
	// sync. Mobile-only: on desktop the bottom bar shows the green WhatsApp
	// button instead, and the profile dropdown would otherwise duplicate it.
	const whatsappItem = {
		classes: {
			icon: '[&>path]:fill-[#18D842]',
			title: 'text-[#18D842]',
		},
		Icon: WhatsappIcon,
		title: NEWSLETTER_WHATSAPP_TEXT,
		newTab: true,
		href: NEWSLETTER_WHATSAPP_LINK,
		className: `lg:hidden ${className}`,
	};

	let siteLinks = $derived(
		showSiteLinks
			? [
					{
						dataCy: 'whatsapp',
						showItemsOnlyOnMobile: true,
						custom: whatsappButton,
					},
					...(!$user
						? [
								{
									dataCy: 'signup',
									buttonProps: {
										title: 'Sign up',
										dataCy: 'signup',
										size: 'sm',
										variant: 'tertiary',
										onclick: handleCreateAccountDialogStores,
									},
									showItemsOnlyOnMobile: true,
									//NEWS TODO: Update icon and style once confirmed by jack
									items: [
										{
											classes: {
												icon: '[&>path]:fill-blue-600 border-b-none',
												title: 'text-blue-600',
											},
											Icon: LogOutIcon,
											title: 'Signup',
											onClick: handleCreateAccountDialogStores,
											className:
												'lg:hidden border-b border-gray-200 [&_p]:hover:no-underline',
										},
									],
								},
							]
						: [
								...(showGlobalNav
									? [
											{
												dataCy: 'download-app',
												showItemsOnlyOnMobile: true,
												linkButtonProps: {
													title: 'Download App',
													dataCy: 'download-app',
													size: 'sm',
													variant: 'tertiary',
													icon: true,
													leading: downloadIcon,
													href: Links.HeniNewsApp,
												},
											},
										]
									: []),
							]),
					!$user
						? {
								dataCy: 'login',
								buttonProps: {
									title: 'Login',
									dataCy: 'login',
									size: 'sm',
									onclick: handleOpenLoginDialog,
								},
								showItemsOnlyOnMobile: true,
								//NEWS TODO: Update icon and style once confirmed by jack
								items: [
									{
										classes: {
											icon: '[&>path]:fill-blue-600 border-b-none',
											title: 'text-blue-600',
										},
										Icon: LogOutIcon,
										title: 'Log In',
										onClick: handleOpenLoginDialog,
										className:
											'lg:hidden border-b border-gray-200 [&_p]:hover:no-underline',
									},
									whatsappItem,
								],
							}
						: {
								dataCy: 'profile',
								children: profileButton,
								showItemsOnlyOnMobile: true,
								items: [
									{
										classes: {
											icon: '[&>path]:fill-blue-600 border-b-none',
											title: 'text-blue-600',
										},
										Icon: LogOutIcon,
										title: 'Log Out',
										onClick: handleLogOut,
										className:
											'lg:hidden border-b border-gray-200 [&_p]:hover:no-underline',
									},
									{
										Icon: RefreshIcon,
										title: 'Change Password',
										onClick: handleOpenChangePasswordDialog,
										className,
									},
									{
										Icon: AccountCloseIcon,
										title: 'Close Account',
										onClick: handleOpenCloseAccountDialog,
										className,
									},
									{
										Icon: MailIcon,
										title: 'HENI Newsletter',
										onClick: handleOpenNewsletterDialog,
										className,
									},
									whatsappItem,
									...(showSavedTags
										? [
												{
													Icon: HeartIcon,
													title: 'Saved Tags',
													className: `${className} sm:hidden`,
													shouldNotCloseOnClick: true,
													onClick: () => {
														savedTagsDrawerStores.states.open.set(true);
													},
												},
											]
										: []),
									...(showGlobalNav
										? [
												{
													Icon: InfoIcon,
													title: 'FAQs',
													newTab: true,
													href: Links.HeniNewsFaq,
													className,
												},
												{
													Icon: TextIcon,
													title: 'Submit Feedback',
													newTab: true,
													href: `${Links.ContactUs}?enquiryType=HENI%20News`,
													className,
												},
											]
										: []),

									{
										Icon: DocumentIcon,
										title: 'Terms of Use',
										newTab: true,
										href: termsHref,
										className,
									},
									{
										Icon: PrivacyIcon,
										title: 'Privacy Policy',
										newTab: true,
										href: privacyHref,
										className,
									},
									{
										classes: {
											icon: '[&>path]:fill-blue-600 border-b-none',
											title: 'text-blue-600',
										},
										Icon: LogOutIcon,
										title: 'Log Out',
										onClick: handleLogOut,
										className: 'hidden lg:flex pb-0 [&_p]:hover:no-underline',
									},
								],
							},
				].filter(Boolean)
			: []
	);
</script>

{#snippet downloadIcon()}
	<DownloadIcon />
{/snippet}
{#snippet whatsappButton()}
	<NewsWhatsappButton dataCy="news" />
{/snippet}
{#snippet profileButton()}
	<div class="flex h-full items-center justify-center">
		<button
			class="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-gray-100"
		>
			<AccountIcon class="h-[18px] w-[18px]" />
		</button>
	</div>
{/snippet}

<NavBar
	popover={mobileMenuPopover}
	{siteLinks}
	{combobox}
	{forceShowGlobalNav}
	keepMobileTriggersInAppMode
	searchComponentProps={{ forceShowGlobalNav }}
	SearchComponent={showSearch ? NewsSearch : undefined}
	isSearchEnabled={showSearch}
	class="fixed w-full"
	classes={{
		bottomBarClasses: {
			siteLinksMenu: 'py-2',
		},
		bottomBar: classNames('!fixed top-12 border-gray-200 !z-30 !lg:z-30', {
			'lg:top-0': !showGlobalNav,
		}),
		topBar: classNames(showGlobalNav ? '' : 'lg:hidden', {
			'[&>div>button+button]:hidden [&>div>button]:mr-0': !showSiteLinks,
		}),
	}}
>
	{#snippet homeIcon()}
		<NewsHeaderIcon />
	{/snippet}
</NavBar>

<SavedTagsDrawer
	dataCy="news"
	drawerStores={savedTagsDrawerStores}
	popover={mobileMenuPopover}
	{forceShowGlobalNav}
/>
{#if authDialogsNeeded}
	{#await import('./AuthDialogs') then { AuthDialogs }}
		<AuthDialogs dataCy="news" {forceShowGlobalNav} />
	{/await}
{/if}
<Toasts />
